if (!customElements.get('koko-balm-results')) {
  customElements.define('koko-balm-results', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.tabs = [...this.querySelectorAll('[data-results-tab]')];
      this.photos = [...this.querySelectorAll('.koko-balm-results__photos')];
      this.copyPanels = [...this.querySelectorAll('.koko-balm-results__panel')];
      this.timeline = this.classList.contains('koko-balm-results--timeline');
      this.current = this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
      const options = { signal: this.events.signal };
      const video = this.querySelector('video');
      if (video && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.videoObserver = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) video.play().catch(() => {});
          else video.pause();
        }, { threshold: 0.25 });
        this.videoObserver.observe(video);
      }
      const desktopHover = matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('pointerenter', event => {
          if (!this.timeline && event.pointerType === 'mouse' && desktopHover.matches) this.select(index);
        }, options);
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('keydown', event => {
          let next = index;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next++;
          else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next--;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else return;
          event.preventDefault();
          next = (next + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        }, options);
      });
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-results-panel]');
        if (panel) this.select(Number(panel.dataset.resultsPanel));
      }, options);
    }
    async select(index) {
      if (!this.tabs[index]) return;
      this.requested = index;
      if (index === this.current) return;
      const incoming = this.photos[index];
      const images = incoming ? [...incoming.querySelectorAll('img')] : [];
      if (images.some(image => !image.complete || !image.naturalWidth)) {
        await Promise.all(images.map(image => {
          image.loading = 'eager';
          return image.decode().catch(() => {});
        }));
        if (!this.isConnected || this.requested !== index) return;
      }
      const previous = this.photos[this.current];
      const previousCopy = this.copyPanels[this.current];
      this.photoAnimation?.cancel();
      this.copyAnimation?.cancel();
      this.copyExitAnimation?.cancel();
      this.copyPanels.forEach(panel => panel.classList.remove('is-copy-previous'));
      this.photos.forEach(photo => photo.classList.remove('is-previous'));
      this.current = index;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.querySelectorAll('[data-results-panel]').forEach(panel => {
        panel.hidden = Number(panel.dataset.resultsPanel) !== index;
      });
      this.photos.forEach((photo, i) => photo.setAttribute('aria-hidden', String(i !== index)));
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.timeline) {
        const strip = this.tabs[index].parentElement;
        const stripBounds = strip.getBoundingClientRect();
        const tabBounds = this.tabs[index].getBoundingClientRect();
        const delta = tabBounds.left < stripBounds.left ? tabBounds.left - stripBounds.left : Math.max(0, tabBounds.right - stripBounds.right);
        if (delta) strip.scrollBy({ left: delta, behavior: reduceMotion ? 'instant' : 'smooth' });
        this.copyPanels.forEach((panel, i) => panel.setAttribute('aria-hidden', String(i !== index)));
        if (!reduceMotion) {
          const timing = { duration: 550, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' };
          this.copyAnimation = this.copyPanels[index].animate(
            [{ transform: 'translateX(105%)' }, { transform: 'translateX(0)' }], timing
          );
          if (previousCopy) {
            previousCopy.classList.add('is-copy-previous');
            this.copyExitAnimation = previousCopy.animate(
              [{ transform: 'translateX(0)' }, { transform: 'translateX(-105%)' }], timing
            );
            this.copyExitAnimation.onfinish = () => previousCopy.classList.remove('is-copy-previous');
          }
        }
      }
      if (!previous || !incoming || reduceMotion) return;
      // Keep the outgoing photo behind the incoming slide, outside grid sizing.
      previous.classList.add('is-previous');
      this.photoAnimation = incoming.animate(
        this.timeline
          ? [{ transform: 'translateX(105%)' }, { transform: 'translateX(0)' }]
          : [{ transform: 'translateY(-100%)' }, { transform: 'translateY(0)' }],
        { duration: this.timeline ? 550 : 1600, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
      );
      this.photoAnimation.onfinish = () => previous.classList.remove('is-previous');
    }
    disconnectedCallback() {
      this.videoObserver?.disconnect();
      this.querySelector('video')?.pause();
      this.requested = null;
      this.photoAnimation?.cancel();
      this.copyAnimation?.cancel();
      this.copyExitAnimation?.cancel();
      this.copyPanels?.forEach(panel => panel.classList.remove('is-copy-previous'));
      this.photos?.forEach(photo => photo.classList.remove('is-previous'));
      this.events?.abort();
      this.events = null;
    }
  });
}
